// src/store/api/propertyContactApi.ts
import { baseApi } from "./baseApi";
import type {
  CreatePropertyContactDto,
  UpdatePropertyContactDto,
  PropertyContactResponseDto,
  PropertyContactFilterDto,
  PaginatedPropertyContactResponseDto,
  PropertyContactStatsDto,
} from "../../types/property-contact";

export const propertyContactApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Property Contact
    createPropertyContact: builder.mutation<PropertyContactResponseDto, CreatePropertyContactDto>({
      query: (data) => ({
        url: "/admin/property-contacts",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PropertyContact"],
    }),

    // Get Property Contacts with filters and pagination
    getPropertyContacts: builder.query<PaginatedPropertyContactResponseDto, PropertyContactFilterDto>({
      query: (params) => ({
        url: "/admin/property-contacts",
        params,
      }),
      transformResponse: (response: any) => {
        // Handle the API response wrapper format
        if (response.data && Array.isArray(response.data)) {
          // API returns data directly as array, transform to expected paginated format
          return {
            data: response.data,
            pagination: {
              page: 1,
              limit: response.data.length,
              total: response.data.length,
              totalPages: 1,
              hasNext: false,
              hasPrev: false,
            }
          };
        }
        if (response.success && response.data) {
          return response.data;
        }
        return response;
      },
      providesTags: (result) =>
        result?.data?.length
          ? [
              ...result.data.map(({ id }) => ({ type: "PropertyContact" as const, id })),
              { type: "PropertyContact", id: "LIST" },
            ]
          : [{ type: "PropertyContact", id: "LIST" }],
    }),

    // Get Property Contact by ID
    getPropertyContactById: builder.query<PropertyContactResponseDto, number>({
      query: (id) => `/admin/property-contacts/${id}`,
      transformResponse: (response: any) => {
        if (response.success && response.data) {
          return response.data;
        }
        return response;
      },
      providesTags: (result, error, id) => [{ type: "PropertyContact", id }],
    }),

    // Update Property Contact
    updatePropertyContact: builder.mutation<
      PropertyContactResponseDto,
      { id: number; data: UpdatePropertyContactDto }
    >({
      query: ({ id, data }) => ({
        url: `/admin/property-contacts/${id}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: any) => {
        if (response.success && response.data) {
          return response.data;
        }
        return response;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "PropertyContact", id },
        { type: "PropertyContact", id: "LIST" },
      ],
    }),

    // Delete Property Contact
    deletePropertyContact: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/admin/property-contacts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "PropertyContact", id },
        { type: "PropertyContact", id: "LIST" },
      ],
    }),

    // Get Property Contact Stats (if needed)
    getPropertyContactStats: builder.query<PropertyContactStatsDto, void>({
      query: () => "/admin/property-contacts/stats",
      providesTags: ["PropertyContact"],
    }),

    // Bulk Delete Property Contacts
    bulkDeletePropertyContacts: builder.mutation<
      { success: boolean; message: string; deletedCount: number },
      { contactIds: number[] }
    >({
      query: (data) => ({
        url: "/admin/property-contacts/bulk/delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["PropertyContact"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreatePropertyContactMutation,
  useGetPropertyContactsQuery,
  useGetPropertyContactByIdQuery,
  useUpdatePropertyContactMutation,
  useDeletePropertyContactMutation,
  useGetPropertyContactStatsQuery,
  useBulkDeletePropertyContactsMutation,
} = propertyContactApi;